(() => {
  if (window.cartSubscriptionDeliveryDateObserver) return;

  const DATE_LABEL = 'お届け希望日';
  const MESSAGE =
    '定期便のため、お届け希望日は指定できません。' +
    '銀行振込の場合は、お申し込み月の月末までにご入金ください。' +
    'ご入金を確認後、翌月からお届け開始となります。';
  const MODULE_ID = 'delivery-datetime--isRender';
  const NOTE_CLASS = 'cart-subscription-delivery-date-note';
  const observedRoots = new WeakSet();
  const observedFrames = new WeakSet();
  const clearedDateControls = new WeakSet();
  const observer = new MutationObserver(() => hideSubscriptionDeliveryDate());
  let deliveryDateClearPromise;
  let deliveryDateClearTimer;
  let allowCheckoutSubmit = false;

  const hasSubscriptionProduct = () =>
    ['#main-cart-footer', 'cart-drawer'].some(
      (selector) =>
        document.querySelector(selector)?.dataset.hasSubscriptionProduct === 'true',
    );

  const normalize = (value) => (value || '').replace(/\s+/g, '').trim();

  const collectElements = (root) => {
    const elements = [...root.querySelectorAll('*')];
    for (const element of elements) {
      if (element.shadowRoot) {
        observeRoot(element.shadowRoot);
        elements.push(...collectElements(element.shadowRoot));
      }

      if (element.tagName !== 'IFRAME') continue;

      if (!observedFrames.has(element)) {
        observedFrames.add(element);
        element.addEventListener('load', () => {
          try {
            const frameDocument = element.contentDocument;
            if (!frameDocument?.documentElement) return;

            observeRoot(frameDocument.documentElement);
            hideSubscriptionDeliveryDate();
          } catch (_error) {
            // Cross-origin frames cannot be inspected from the theme.
          }
        });
      }

      try {
        const frameDocument = element.contentDocument;
        if (!frameDocument?.documentElement) continue;

        observeRoot(frameDocument.documentElement);
        elements.push(...collectElements(frameDocument));
      } catch (_error) {
        // Cross-origin frames cannot be inspected from the theme.
      }
    }
    return elements;
  };

  const observeRoot = (root) => {
    if (observedRoots.has(root)) return;

    observedRoots.add(root);
    observer.observe(root, { childList: true, subtree: true });
  };

  const clearStoredDeliveryDate = () => {
    if (deliveryDateClearPromise) return deliveryDateClearPromise;

    const root = window.Shopify?.routes?.root || '/';
    deliveryDateClearPromise = fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error('Could not read the cart.');
        return response.json();
      })
      .then((cart) => {
        const attributes = cart.attributes || {};
        if (
          !attributes.delivery_date &&
          !attributes['配送希望日'] &&
          attributes['配送日の指定'] !== 'あり'
        ) return;

        return fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            attributes: {
              delivery_date: '',
              '配送希望日': '',
              '配送日の指定': 'なし',
            },
          }),
        }).then((response) => {
          if (!response.ok) throw new Error('Could not clear the delivery date.');
          return response.json();
        }).then((updatedCart) => {
          if (
            updatedCart.attributes?.delivery_date ||
            updatedCart.attributes?.['配送希望日'] ||
            updatedCart.attributes?.['配送日の指定'] === 'あり'
          ) {
            throw new Error('The delivery date is still set on the cart.');
          }
        });
      })
      .finally(() => {
        deliveryDateClearPromise = undefined;
      });

    return deliveryDateClearPromise;
  };

  const scheduleDeliveryDateClear = () => {
    window.clearTimeout(deliveryDateClearTimer);
    deliveryDateClearTimer = window.setTimeout(() => {
      if (hasSubscriptionProduct()) clearStoredDeliveryDate().catch(() => {});
    }, 250);
  };

  document.addEventListener(
    'click',
    (event) => {
      const checkoutButton = event.target.closest(
        '#checkout, button[name="checkout"], input[name="checkout"]',
      );
      if (
        !checkoutButton ||
        !hasSubscriptionProduct() ||
        allowCheckoutSubmit
      ) {
        allowCheckoutSubmit = false;
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      checkoutButton.disabled = true;
      clearStoredDeliveryDate()
        .then(() => {
          allowCheckoutSubmit = true;
          checkoutButton.disabled = false;
          if (checkoutButton.form) {
            checkoutButton.form.requestSubmit(checkoutButton);
          } else {
            checkoutButton.click();
          }
        })
        .catch(() => {
          checkoutButton.disabled = false;
          window.alert('配送希望日の更新に失敗しました。ページを再読み込みして、もう一度お試しください。');
        });
    },
    true,
  );

  const hideSubscriptionDeliveryDate = () => {
    if (!hasSubscriptionProduct()) return;

    // AMP can restore its saved date while rendering after the cart loads.
    // Recheck after its DOM updates, and clear both AMP's date and legacy labels.
    scheduleDeliveryDateClear();

    const cartFooter = document.querySelector('#main-cart-footer');
    if (!cartFooter || cartFooter.dataset.hasSubscriptionProduct !== 'true') return;

    const deliveryModule = collectElements(document).find((element) => element.id === MODULE_ID);
    if (!deliveryModule) return;

    const dateLabel = collectElements(deliveryModule).find((element) =>
      normalize(element.textContent) === DATE_LABEL,
    );
    const dateField = dateLabel?.nextElementSibling;
    if (!dateLabel || !dateField) return;

    // Reset AMP's own selection so changing the time cannot submit its old date.
    const clearDateControl = dateField.querySelector('.delivery-select-container--close[role="button"]');
    if (clearDateControl && !clearedDateControls.has(clearDateControl)) {
      clearedDateControls.add(clearDateControl);
      clearDateControl.click();
    }
    if (dateLabel.dataset.subscriptionDateHidden === 'true') return;

    for (const element of [dateLabel, dateField]) {
      element.hidden = true;
      element.style.setProperty('display', 'none', 'important');
    }
    dateLabel.dataset.subscriptionDateHidden = 'true';

    const note = dateField.ownerDocument.createElement('p');
    note.className = NOTE_CLASS;
    note.textContent = MESSAGE;
    note.setAttribute('role', 'note');
    note.style.marginBlock = '0 1.5rem';
    dateField.insertAdjacentElement('afterend', note);
  };

  observeRoot(document.documentElement);
  window.cartSubscriptionDeliveryDateObserver = observer;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideSubscriptionDeliveryDate, { once: true });
  } else {
    hideSubscriptionDeliveryDate();
  }
})();

// 定期便がカートにあるとき、どこポイのポイント利用欄を隠す。
// 定期便はポイント利用の対象外だが、アプリ側は入力欄を出し続けるため、
// 配送希望日を隠すときと同じ判定（data-has-subscription-product）で制御する。
(() => {
  if (window.cartSubscriptionDokopoiObserver) return;

  const BLOCK_SELECTOR = '[id*="dokopoi_cart_v2"]';
  const NOTE_CLASS = 'cart-subscription-dokopoi-note';
  const MESSAGE = '定期便はポイントのご利用対象外です。';
  const HIDDEN_FLAG = 'subscriptionDokopoiHidden';

  const hasSubscriptionProduct = () =>
    ['#main-cart-footer', 'cart-drawer'].some(
      (selector) =>
        document.querySelector(selector)?.dataset.hasSubscriptionProduct === 'true',
    );

  const applyDokopoiVisibility = () => {
    const blocks = document.querySelectorAll(BLOCK_SELECTOR);
    if (!blocks.length) return;

    const shouldHide = hasSubscriptionProduct();

    for (const block of blocks) {
      if (shouldHide) {
        if (block.dataset[HIDDEN_FLAG] === 'true') continue;

        block.dataset[HIDDEN_FLAG] = 'true';
        block.hidden = true;
        block.style.setProperty('display', 'none', 'important');

        const note = block.ownerDocument.createElement('p');
        note.className = NOTE_CLASS;
        note.textContent = MESSAGE;
        note.setAttribute('role', 'note');
        note.style.marginBlock = '0 1.5rem';
        block.insertAdjacentElement('afterend', note);
        continue;
      }

      if (block.dataset[HIDDEN_FLAG] !== 'true') continue;

      // 定期便がカートから外れたら元に戻す。
      delete block.dataset[HIDDEN_FLAG];
      block.hidden = false;
      block.style.removeProperty('display');

      const note = block.nextElementSibling;
      if (note && note.classList.contains(NOTE_CLASS)) note.remove();
    }
  };

  const dokopoiObserver = new MutationObserver(applyDokopoiVisibility);
  dokopoiObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.cartSubscriptionDokopoiObserver = dokopoiObserver;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyDokopoiVisibility, { once: true });
  } else {
    applyDokopoiVisibility();
  }
})();
